import SearchIcon from "@mui/icons-material/Search";
import { Fragment, useEffect, useState } from "react";

import { Autocomplete } from "@/Components/v2/atoms/Autocomplete";
import { Box } from "@/Components/v2/atoms/Box";
import { Checkbox } from "@/Components/v2/atoms/Checkbox";
import { Divider } from "@/Components/v2/atoms/Divider";
import { InputAdornment } from "@/Components/v2/atoms/InputAdornment";
import { Stack } from "@/Components/v2/atoms/Stack";
import { TextField } from "@/Components/v2/atoms/TextField";
import { Typography } from "@/Components/v2/atoms/Typography";
import { Option } from "@/Components/v2/molecule/LabelledAutoComplete/types";

const SELECT_ALL = "select-all";

interface LabelledAutoCompleteProps {
  label: string;
  multiple?: boolean;
  checkbox?: boolean;
  id: string;
  options?: Option[];
  selectedOptions: Option[];
  onSelectChange: (selected: Option[]) => void;
  disabled?: boolean;
  disableClearable?: boolean;
  allOptionsLabel?: string;
  width?: number;
}

export const LabelledAutoComplete: React.FC<LabelledAutoCompleteProps> = ({
  label,
  id,
  multiple,
  checkbox = true,
  selectedOptions,
  onSelectChange,
  options,
  disabled = false,
  disableClearable,
  allOptionsLabel,
  width = 280,
}) => {
  const [selectAll, setSelectAll] = useState(false);

  const selectionForAutocomplete = multiple ? selectedOptions : selectedOptions?.[0] || null;

  // Check if all options are selected on initial load or when options/selectedOptions change
  useEffect(() => {
    if (options && options.length > 0 && selectedOptions.length === options.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [options, selectedOptions]);

  // Prepend "Select all" checkbox to the options
  const optionsWithSelectAll = multiple
    ? [{ label: allOptionsLabel || `All ${label}`, value: SELECT_ALL }, ...(options || [])]
    : options;

  const handleSelection = (selected: Option[] | Option) => {
    console.log("selected inside labelled auto complete", selected);
    if (selected === null || selected === undefined) {
      return;
    }

    const selectedArray = Array.isArray(selected) ? selected : [selected];
    onSelectChange(selectedArray);
    setSelectAll(selectedArray.length === (options || []).length);
  };

  const handleSelectAll = (
    _event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLDivElement>
  ) => {
    if (!selectAll) {
      onSelectChange([...(options || [])]);
    } else {
      onSelectChange([]);
    }
    setSelectAll(!selectAll);
  };

  if (!options || options.length === 0) {
    return (
      <Stack>
        <Typography variant="body1" mb={1} fontWeight={600}>
          {label}
        </Typography>
        <TextField
          disabled
          fullWidth
          size="small"
          placeholder="No Data"
          InputProps={{
            startAdornment: <SearchIcon style={{ marginRight: 4 }} />,
          }}
        />
      </Stack>
    );
  }

  return (
    <Stack>
      <Typography variant="body1" mb={1} fontWeight={600}>
        {label}
      </Typography>
      <Autocomplete
        disabled={!!disabled}
        disableClearable={disableClearable}
        multiple={multiple}
        id={id}
        options={optionsWithSelectAll || []}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label || ""}
        renderOption={(props, option, { selected }) => {
          if (option.value === SELECT_ALL && multiple) {
            return (
              <Fragment key={SELECT_ALL}>
                <li
                  {...props}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Box onClick={handleSelectAll} sx={{ cursor: "pointer" }}>
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      style={{ marginRight: 8 }}
                    />
                    {option.label}
                  </Box>
                </li>
                <Divider sx={{ marginY: "0.5rem" }} />
              </Fragment>
            );
          }
          return (
            <li {...props} key={option.value}>
              {checkbox && <Checkbox style={{ marginRight: 8 }} checked={selected} />}
              {option.label}
            </li>
          );
        }}
        style={{ width: width }}
        value={selectionForAutocomplete}
        onChange={(event, value) => handleSelection(value)}
        renderInput={(params) => (
          <TextField
            {...params}
            id={id}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <SearchIcon />
                  {multiple && selectedOptions.length > 0 && (
                    <InputAdornment position="end">
                      <Typography>{generatePlaceholderText(selectedOptions)}</Typography>
                    </InputAdornment>
                  )}
                </>
              ),
            }}
            placeholder={selectedOptions.length === 0 ? `Select ${label}` : ""}
            disabled={disabled}
            fullWidth
            size="small"
            InputLabelProps={{}}
          />
        )}
      />
    </Stack>
  );
};

export default LabelledAutoComplete;

const generatePlaceholderText = (selectedOptions: Option[]) => {
  if (selectedOptions.length === 1) {
    return selectedOptions[0]!.label;
  } else {
    return `${selectedOptions.length} selected`;
  }
};
